/*  iceberg-ui  */
import { Flex, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Typography } from '@mui/material';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';
import { Route } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

export interface Path {
    key: string;
    label: string;
    onClick?: () => void;
    route: string;
}

export const Breadcrumbs = ({
    breadcrumbPath,
    fontSize = FontSizes[1]
}: {
    breadcrumbPath?: Path[] | null;
    fontSize?: number;
}) => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();

    const buildBaseRoute = Route.buildBaseRoute({
        clientId,
        partnerId
    });

    if (!breadcrumbPath?.length) {
        return null;
    }

    if (typeof fontSize !== 'number') {
        debug.w(
            'Breadcrumbs: Invalid fontSize provided, falling back to default.'
        );
        const defaultFontSize = FontSizes[1];
        // eslint-disable-next-line no-param-reassign
        fontSize = defaultFontSize;
    }

    const numberOfBreadcrumbs = breadcrumbPath?.length;

    return (
        <Flex
            data-testid="breadcrumbsContainer"
            sx={{ alignItems: 'center', flex: 1 }}
        >
            {breadcrumbPath?.map((path, index) => {
                const { key, label, onClick, route } = path;

                if (!label) return null;

                const isExternalLink = route?.startsWith('http');
                const isPartialPath = route?.startsWith('/');
                const to = isPartialPath ? `${buildBaseRoute}${route}` : route;
                const notLastBreadcrumb = index + 1 < numberOfBreadcrumbs;

                const text = (
                    <Typography
                        aria-label={`breadcrumb_${label}`}
                        data-testid={`breadcrumb_${key}`}
                        data-trackid={`breadcrumb_${key}`}
                        key={key}
                        variant="span"
                        sx={{
                            ...(notLastBreadcrumb ? { cursor: 'pointer' } : {}),
                            fontSize
                        }}
                    >
                        {t(label)}
                    </Typography>
                );

                return notLastBreadcrumb ? (
                    <React.Fragment key={key}>
                        <Link
                            aria-label={`breadcrumb_${label}_link`}
                            className="link underline"
                            data-trackid={`breadcrumb_${key}_routeTo`}
                            {...(isExternalLink
                                ? {
                                      rel: 'noopener noreferrer nofollow',
                                      target: '_blank'
                                  }
                                : {})}
                            {...(isPartialPath ? {} : { relative: 'path' })}
                            onClick={onClick}
                            to={to}
                        >
                            {text}
                        </Link>
                        <IconWithVariant
                            data-testid="breadcrumbs_icon"
                            size={fontSize}
                            src="ChevronRight"
                            variant="textColor"
                            wrapperProps={{
                                paddingLeft: '16px',
                                paddingRight: '16px'
                            }}
                        />
                    </React.Fragment>
                ) : (
                    text
                );
            })}
        </Flex>
    );
};
